import { DEPLOY_LIST_DAYS, DeploymentsList } from '@/components/deploys/DeploymentsList';
import { CodeSnippet } from '@/components/primitives/CodeSnippet';
import { EmptyState } from '@/components/primitives/EmptyState';
/**
 * Settings → Deploys (spec plans/1.0.0/deploy-markers/05-dashboard.md).
 *
 * State-aware IA: the page has two jobs and shows whichever the site needs.
 *  - No deploys yet → setup is the hero (CLI recommended, signed webhook
 *    below as the advanced fallback).
 *  - Deploys flowing → the deployments list leads; setup + the signed
 *    webhook recede into a collapsed disclosure so they stop competing
 *    with the data the user came to see.
 *
 * Settings child routes render into the settings layout's Outlet — no
 * Island here. Site context is the global active site (not a route param).
 */
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useActiveSiteUuid } from '@/lib/active-site';
import { toastSuccess } from '@/lib/errors';
import {
  type DeployConfig,
  type DeployConfigPatchRequest,
  useDeployAuditLog,
  useDeployConfig,
  useSiteDeploys,
  useUpdateDeployConfig,
} from '@/lib/queries';
import { cn } from '@/lib/utils';
import { Browsers, CaretDown, Warning } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, type ReactNode, useState } from 'react';

export const Route = createFileRoute('/_app/settings/deploys')({ component: DeploysSettings });

const TTL_OPTIONS = [6, 12, 24, 48, 72];
const WINDOW_OPTIONS = [7, 14, 30];

function DeploysSettings(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  const config = useDeployConfig(siteUuid);
  // Shares DeploymentsList's query (same days → same cache key) so reading
  // the count here costs no extra request; it just drives the layout.
  const deploys = useSiteDeploys(siteUuid, DEPLOY_LIST_DAYS);
  const auditLog = useDeployAuditLog(siteUuid);
  const update = useUpdateDeployConfig();
  // The secret is only returned on enable/rotate — show it once.
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  async function patch(body: DeployConfigPatchRequest): Promise<void> {
    if (!siteUuid) return;
    const res = await update.mutateAsync({ siteUuid, body });
    if (res.secret) {
      setRevealedSecret(res.secret);
      toastSuccess('Secret generated', 'Copy it now — it is shown only once.');
    }
  }

  if (!siteUuid) {
    return (
      <div className="space-y-6">
        <Header />
        <EmptyState
          icon={Browsers}
          title="Select a site"
          description="Choose a site from the switcher to configure deploy tracking."
        />
      </div>
    );
  }

  const cfg = config.data;
  const enabled = cfg?.enabled ?? false;

  // Onboarding only when we know the site has no deploys. While the list is
  // still loading or errored we keep the "active" layout so onboarding never
  // masks a populated list or a load error (which DeploymentsList surfaces).
  const hasDeploys = (deploys.data?.length ?? 0) > 0;
  const showOnboarding = !deploys.isLoading && !deploys.isError && !hasDeploys;

  const cli = <CliSetup siteUuid={siteUuid} />;
  const webhook = (
    <SignedWebhook
      cfg={cfg}
      enabled={enabled}
      loading={config.isLoading}
      pending={update.isPending}
      revealedSecret={revealedSecret}
      onPatch={patch}
    />
  );

  return (
    <div className="space-y-6">
      <Header />

      {showOnboarding ? (
        <>
          <section className="space-y-3">
            <div>
              <h3 className="text-base font-semibold text-[var(--color-ink)]">
                Set up deploy tracking
              </h3>
              <p className="text-sm text-[var(--color-mute)]">
                Record a marker on every release so Clickweave can show the before/after performance
                impact. The Clickweave CLI is the simplest path — it authenticates with an API key,
                so there's no signing secret to manage or share.
              </p>
            </div>
            {cli}
          </section>
          {webhook}
        </>
      ) : (
        <>
          <DeploymentsList siteUuid={siteUuid} />
          <SetupDisclosure webhookEnabled={enabled}>
            {cli}
            {webhook}
          </SetupDisclosure>
        </>
      )}

      {enabled && <RecentWebhookEvents auditLog={auditLog} />}
    </div>
  );
}

function Header(): ReactElement {
  return (
    <header>
      <h2 className="text-base font-semibold text-[var(--color-ink)]">Deploys</h2>
      <p className="text-sm text-[var(--color-mute)]">
        Mark deploys so Clickweave can show the before/after performance impact on every release.
      </p>
    </header>
  );
}

/** The recommended CLI path — a self-labelled card reused in both states. */
function CliSetup({ siteUuid }: { siteUuid: string }): ReactElement {
  return (
    <div className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
      <p className="mb-2 text-sm font-medium text-[var(--color-ink)]">
        Recommended — Clickweave CLI
      </p>
      <CodeSnippet
        title="CI step"
        code={`clickweave deploys create \\\n  --site ${siteUuid} \\\n  --commit-sha "$GIT_SHA" \\\n  --branch "$GIT_BRANCH" \\\n  --provider github-actions`}
      />
      <p className="mt-2 text-xs text-[var(--color-mute)]">
        Authenticate with a <code>deploys:write</code> API key via the{' '}
        <code>CLICKWEAVE_API_KEY</code> environment variable — create one in Settings → API keys.
      </p>
    </div>
  );
}

/** The HMAC-signed webhook — the advanced fallback to the CLI. */
function SignedWebhook({
  cfg,
  enabled,
  loading,
  pending,
  revealedSecret,
  onPatch,
}: {
  cfg: DeployConfig | undefined;
  enabled: boolean;
  loading: boolean;
  pending: boolean;
  revealedSecret: string | null;
  onPatch: (body: DeployConfigPatchRequest) => Promise<void>;
}): ReactElement {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-[var(--color-ink)]">
            Signed webhook (advanced)
          </h3>
          <p className="text-sm text-[var(--color-mute)]">
            Prefer a raw HTTP call from your CD platform? Enable an HMAC-signed webhook instead.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[var(--color-mute)]">
            {enabled ? 'Enabled' : 'Disabled'}
          </span>
          <Switch
            checked={enabled}
            disabled={pending || loading}
            onCheckedChange={(next) => {
              void onPatch({ enabled: next });
            }}
          />
        </div>
      </div>

      {enabled && cfg && (
        <div className="space-y-5 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
          {revealedSecret && (
            <div className="rounded-xl border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/5 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-ink)]">
                <Warning aria-hidden="true" />
                Save this signing secret — it's shown only once.
              </div>
              <CodeSnippet className="mt-2" code={revealedSecret} />
            </div>
          )}

          <CodeSnippet title="Webhook URL" code={cfg.webhook_url} />

          <div className="flex flex-wrap items-end gap-4">
            <WindowSelect
              label="Baseline window (pre-deploy)"
              value={cfg.control_window_days}
              options={WINDOW_OPTIONS}
              unit="days"
              onChange={(v) => void onPatch({ enabled: true, control_window_days: v })}
            />
            <WindowSelect
              label="Comparison window (post-deploy)"
              value={cfg.default_ttl_hours}
              options={TTL_OPTIONS}
              unit="hours"
              onChange={(v) => void onPatch({ enabled: true, default_ttl_hours: v })}
            />
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => void onPatch({ enabled: true, rotate_secret: true })}
            >
              Rotate secret
            </Button>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-[var(--color-ink)]">
              Signed request example
            </p>
            <CodeSnippet title="curl" code={cfg.setup.curl} variables={{ WHSEC: '$WHSEC' }} />
            <p className="mt-2 text-xs text-[var(--color-mute)]">
              Set <code>WHSEC</code> to your signing secret as a CI secret — never commit or paste
              it. Most teams should use the CLI above instead.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Collapsed setup disclosure shown once deploys are flowing — keeps the
 * CLI + signed-webhook config reachable without letting it outrank the list.
 */
function SetupDisclosure({
  webhookEnabled,
  children,
}: {
  webhookEnabled: boolean;
  children: ReactNode;
}): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          render={
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
            >
              <div className="space-y-1">
                <h3 className="text-base font-semibold text-[var(--color-ink)]">Deploy setup</h3>
                <p className="text-sm text-[var(--color-mute)]">
                  Add another CI source or configure the signed webhook.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {webhookEnabled && (
                  <span className="text-xs text-[var(--color-mute)]">Webhook enabled</span>
                )}
                <CaretDown
                  aria-hidden="true"
                  className={cn('shrink-0 transition-transform', open ? 'rotate-180' : 'rotate-0')}
                />
              </div>
            </button>
          }
        />
        <CollapsibleContent>
          <div className="space-y-6 border-t border-[var(--color-line-soft)] px-5 py-5">
            {children}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

function RecentWebhookEvents({
  auditLog,
}: {
  auditLog: ReturnType<typeof useDeployAuditLog>;
}): ReactElement {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold text-[var(--color-ink)]">Recent webhook events</h3>
        <p className="text-sm text-[var(--color-mute)]">
          Every delivery attempt — useful for debugging setup.
        </p>
      </div>
      <div className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
        {auditLog.isLoading && <Skeleton className="h-24 w-full" />}
        {!auditLog.isLoading && (auditLog.data ?? []).length === 0 && (
          <p className="text-sm text-[var(--color-mute)]">No webhook attempts yet.</p>
        )}
        {(auditLog.data ?? []).length > 0 && (
          <ul className="divide-y divide-[var(--color-line-soft)]">
            {(auditLog.data ?? []).slice(0, 20).map((e) => (
              <li key={e.uuid} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="text-[var(--color-mute)]">
                  {new Date(e.received_at).toLocaleString()}
                </span>
                <span className="flex items-center gap-2">
                  <StatusPill code={e.status_code} />
                  {e.error_code && (
                    <span className="text-xs text-[var(--color-mute)]">{e.error_code}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function WindowSelect({
  label,
  value,
  options,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  options: number[];
  unit: string;
  onChange: (v: number) => void;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1 text-sm">
      <span className="text-[var(--color-mute)]">{label}</span>
      <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
        <SelectTrigger className="w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectPopup>
          {options.map((o) => (
            <SelectItem key={o} value={String(o)}>
              {o} {unit}
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>
    </div>
  );
}

function StatusPill({ code }: { code: number }): ReactElement {
  const ok = code >= 200 && code < 300;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
        ok
          ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
          : 'bg-[var(--color-danger)]/10 text-[var(--color-danger)]'
      }`}
    >
      {code}
    </span>
  );
}
